import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  OfferPublished,
  ReinsurersParticipation,
} from "graphql/__generated__/globalTypes";
import { FC, Fragment, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  offer: OfferPublished;
  isEditing: boolean;
  currentParticipation: ReinsurersParticipation | null;
  onSubmit: (share: number, conditions: string) => void;
  onEdit: (share: number) => void;
  isOfferClosed?: boolean;
  loading: boolean;
}

const ParticipationModal: FC<Props> = ({
  isOpen,
  onClose,
  offer,
  isEditing,
  currentParticipation,
  onSubmit,
  onEdit,
  isOfferClosed = false,
  loading,
}) => {
  const [share, setShare] = useState<string>("");
  const [conditions, setConditions] = useState<string>("");
  const [shareInputTouched, setShareInputTouched] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const totalFacOffer = offer?.offer?.facultative_offer || 0;
  const hasParticipated = !!offer.reinsurer_participation;
  const isExpired = offer.status.toLocaleUpperCase() === "EXPIRED";
  const isPlaceItOffer = offer.published_by?.toLowerCase() === "placeit";

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
  ];

  const findConditionMessage = (transactionThread: any[]) => {
    if (!transactionThread || !Array.isArray(transactionThread)) {
      return "";
    }

    const conditionMessage = transactionThread.find(
      (message) => message.is_condition === true
    );

    return conditionMessage?.message || "";
  };

  useEffect(() => {
    if (isEditing && currentParticipation?.offer_participant_percentage) {
      setShare(currentParticipation.offer_participant_percentage.toString());

      const conditionMsg = findConditionMessage(
        currentParticipation?.transaction_thread || []
      );
      setConditions(conditionMsg);
    } else {
      setShare("");
      setConditions("");
    }
  }, [isEditing, currentParticipation, isOpen]);

  const getLoyaltyMessage = () => {
    if (hasParticipated) {
      return "You will earn loyalty points on this offer.";
    }

    return isExpired
      ? "No loyalty points will be awarded."
      : `Participate before the timer ends to earn loyalty points!`;
  };

  const handleShareChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.startsWith("-")) return;

    setShare(value);

    const numericValue = parseFloat(value);

    if (value && isNaN(numericValue)) {
      setError("Please enter a valid number");
    } else if (numericValue < 0) {
      setError("Share cannot be negative");
    } else if (numericValue > totalFacOffer) {
      setError(
        `Share cannot exceed the total facultative offer of ${totalFacOffer}%`
      );
    } else {
      setError(null);
    }
  };

  const handleShareFocus = () => {
    setShareInputTouched(true);
    if (share === "0") {
      setShare("");
    }
  };

  const handleSubmit = () => {
    setError(null);
    const numericValue = parseFloat(share);

    if (isOfferClosed) {
      setError("Cannot modify participation for closed offers");
      return;
    }
    if (!share || isNaN(numericValue) || numericValue <= 0) {
      setError("Share must be greater than 0");
      return;
    }

    if (numericValue > totalFacOffer) {
      setError(
        `Share cannot exceed the total facultative offer of ${totalFacOffer}%`
      );
      return;
    }

    if (isEditing) {
      onEdit(numericValue);
    } else {
      onSubmit(numericValue, conditions);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isEditing ? "Edit Participation" : "Participate in Offer"}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-md">
                  <p className="text-sm">{getLoyaltyMessage()}</p>
                </div>

                {isOfferClosed && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-md">
                    <p className="text-sm">
                      This offer is closed. Participation cannot be modified.
                    </p>
                  </div>
                )}
                {offer && (
                  <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-1">
                      Total Facultative Offer
                    </div>
                    <div className="text-lg font-semibold">
                      {totalFacOffer}%
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label
                    htmlFor="share"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Share (%)
                  </label>
                  <input
                    type="number"
                    name="share"
                    id="share"
                    min="0"
                    max={totalFacOffer}
                    step="0.01"
                    className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter percentage"
                    value={share}
                    onChange={handleShareChange}
                    onFocus={handleShareFocus}
                    disabled={isOfferClosed}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e") {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                {error && (
                  <div className="mt-2 text-sm text-red-600">{error}</div>
                )}

                <div className="mb-4">
                  <label
                    htmlFor="conditions"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Conditions {isEditing ? "" : "(Optional)"}
                  </label>

                  {isEditing ? (
                    <div className="border border-gray-300 rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: conditions }}
                      />
                    </div>
                  ) : (
                    <div className="rounded-md border border-gray-300 overflow-hidden">
                      <style>{`
                        .ql-editor {
                          min-height: 100px !important;
                          max-height: 200px !important;
                          overflow-y: auto;
                        }
                      `}</style>
                      <ReactQuill
                        value={conditions}
                        onChange={setConditions}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Enter any conditions for your participation"
                        style={{
                          backgroundColor: "white",
                        }}
                        readOnly={isOfferClosed}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={isOfferClosed}
                  >
                    {loading
                      ? "Loading..."
                      : isEditing
                      ? "Save Changes"
                      : "Submit Participation"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ParticipationModal;
